import React, { useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Stream } from '../services/streamService';
import streamService from '../services/streamService';
import deviceService from '../services/deviceService';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface StreamItemProps {
  stream: Stream;
  onDelete: (streamId: number) => void;
  onUpdate: (stream: Stream) => void;
  isDeleting: boolean;
  priorityRank: number;
  deviceId: number;
  deviceType?: string;
}

const StreamItem: React.FC<StreamItemProps> = ({ stream, onDelete, onUpdate, isDeleting, priorityRank, deviceId, deviceType }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editColor, setEditColor] = useState(`#${stream.red.toString(16).padStart(2, '0')}${stream.green.toString(16).padStart(2, '0')}${stream.blue.toString(16).padStart(2, '0')}`);
  const [editBlink, setEditBlink] = useState(stream.blink);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stream.id });

  const getChannelTypeColor = (type: 'twitch' | 'youtube' | 'kick') => {
    return type === 'twitch' ? '#9146ff' : type === 'youtube' ? '#ff0000' : '#53fc18';
  };

  const getChannelLogo = (type: 'twitch' | 'youtube' | 'kick') => {
    if (type === 'twitch') {
      return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
        </svg>
      );
    } else if (type === 'youtube') {
      return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      );
    } else {
      return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3.2 5.4C3.2 3.1 5.1 1.2 7.4 1.2h9.2c2.3 0 4.2 1.9 4.2 4.2v13.2c0 2.3-1.9 4.2-4.2 4.2H7.4c-2.3 0-4.2-1.9-4.2-4.2V5.4zm9.7 5.8l-.1-.8c-.1-.9-.2-1.8-.5-2.7-.3-.9-.9-1.7-1.8-2-1.1-.4-2.4-.2-3.4.5-.3.2-.5.5-.7.8l-.3.6V5.2c0-.4-.3-.7-.7-.7s-.7.3-.7.7v8.4c0 .4.3.7.7.7s.7-.3.7-.7v-2.2c0-.9.3-1.7.9-2.3.6-.6 1.5-.9 2.4-.7.5.1.9.5 1.1.9.2.5.3 1.1.4 1.7l.1.8c.1.9.2 1.9.6 2.7.4.9 1.2 1.5 2.1 1.6.9.1 1.8-.2 2.6-.7 1.5-1 2.3-2.7 2.3-4.5 0-.4-.3-.7-.7-.7s-.7.3-.7.7c0 1.4-.6 2.7-1.8 3.5-.5.3-1.1.5-1.7.4-.5-.1-.9-.4-1.1-.9-.2-.5-.3-1.1-.4-1.7z" />
        </svg>
      );
    }
  };

  const handleToggleMute = async () => {
    setIsUpdating(true);
    try {
      const updated = await streamService.update(stream.id, { muted: !stream.muted });
      onUpdate(updated);
      // Immediately refresh the device LED to reflect the mute change
      await deviceService.refreshDeviceLED(deviceId);
    } catch (err) {
      console.error('Failed to toggle mute:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleWatching = async () => {
    setIsUpdating(true);
    try {
      const updated = await streamService.update(stream.id, { watching: !stream.watching });
      onUpdate(updated);
      // Immediately refresh the device LED to reflect the watching change
      await deviceService.refreshDeviceLED(deviceId);
    } catch (err) {
      console.error('Failed to toggle watching:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStreamClick = () => {
    // Don't open modal for locked streams
    if (stream.locked) {
      return;
    }
    setShowModal(true);
  };

  const handleSaveColor = async () => {
    setIsUpdating(true);
    try {
      const updateData: any = {
        blink: editBlink,
      };

      // Only update color for RGB devices
      if (deviceType === 'RGB') {
        const r = parseInt(editColor.slice(1, 3), 16);
        const g = parseInt(editColor.slice(3, 5), 16);
        const b = parseInt(editColor.slice(5, 7), 16);
        updateData.red = r;
        updateData.green = g;
        updateData.blue = b;
      }

      const updated = await streamService.update(stream.id, updateData);
      onUpdate(updated);
      await deviceService.refreshDeviceLED(deviceId);
      setShowModal(false);
    } catch (err) {
      console.error('Failed to update stream color:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditColor(`#${stream.red.toString(16).padStart(2, '0')}${stream.green.toString(16).padStart(2, '0')}${stream.blue.toString(16).padStart(2, '0')}`);
    setEditBlink(stream.blink);
    setShowModal(false);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className="stream-item"
        {...attributes}
        {...listeners}
      >
        <div
          style={{
            opacity: stream.muted ? 0.6 : 1,
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            width: '100%',
            cursor: stream.locked ? 'default' : 'pointer',
          }}
          onClick={handleStreamClick}
        >
          <div className="stream-info" style={{ flex: 1, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div className="stream-header">
                {stream.profilePicture && (
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      flexShrink: 0,
                      border: Boolean(stream.islive) ? '3px solid rgba(255, 0, 0, 1)' : 'white 2px solid',
                      boxShadow: Boolean(stream.islive) ? '0 0 8px rgba(255, 0, 0, 0.6)' : 'none',
                      display: 'inline-block',
                      animation: Boolean(stream.islive) ? 'blink-border 1.5s ease-in-out infinite' : 'none',
                      marginBottom: 0,
                    }}
                  >
                    <img
                      src={stream.profilePicture}
                      alt={stream.channelName || stream.channelID}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                    <span
                      className="channel-type-badge"
                      style={{
                        position: 'relative',
                        bottom: 15,
                        left: 45,
                        transform: 'translateX(-50%)',
                        backgroundColor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        padding: 2,
                        width: 24,
                        height: 24,
                      }}
                      title={stream.channelType === 'twitch' ? 'Twitch' : stream.channelType === 'youtube' ? 'YouTube' : 'Kick'}
                    >
                      {getChannelLogo(stream.channelType)}
                    </span>
                  </div>
                )}

                <span
                  className="stream-channel"
                  style={{
                    fontWeight: 600,
                  }}
                >
                  {stream.channelName || `Channel ${stream.channelID}`}
                  {Boolean(stream.locked) && (
                    <span
                      style={{
                        marginLeft: 8,
                        padding: '4px 8px',
                        borderRadius: 4,
                        backgroundColor: '#6c757d',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                      title="Locked stream - cannot be modified or deleted"
                    >
                      🔒 LOCKED
                    </span>
                  )}
                </span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      backgroundColor: `rgb(${stream.red}, ${stream.green}, ${stream.blue})`,
                      border: '2px solid var(--border-color)',
                      cursor: stream.locked ? 'default' : 'pointer',
                      transition: 'transform 0.2s',
                      opacity: stream.locked ? 0.7 : 1,
                    }}
                    title={stream.locked ? 'Locked stream - color cannot be changed' : `Color: RGB(${stream.red}, ${stream.green}, ${stream.blue})`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!stream.locked) {
                        handleStreamClick();
                      }
                    }}
                  />
                  {Boolean(stream.blink) && (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#ffc107',
                        padding: '2px 6px',
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 193, 7, 0.1)',
                        border: '1px solid #ffc107',
                      }}
                      title="Blinking enabled"
                    >
                      ✨
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, alignItems: 'flex-end' }}>
            {Boolean(stream.islive) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleWatching();
                  }}
                  disabled={isUpdating}
                  className={`toggle-switch ${stream.watching ? 'active' : ''}`}
                  style={{
                    cursor: isUpdating ? 'not-allowed' : 'pointer',
                    opacity: isUpdating ? 0.6 : 1,
                  }}
                  title={stream.watching ? 'Mark as unwatched (re-enable notification)' : 'Mark as watching (clear notification)'}
                  type="button"
                >
                  <span className="toggle-circle"></span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={handleCancelEdit}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-card)',
              padding: 30,
              borderRadius: 12,
              maxWidth: 400,
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: 20, color: 'var(--text-primary)' }}>
              Edit Stream Settings
            </h3>

            {deviceType === 'RGB' && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, color: 'var(--text-primary)', fontWeight: 600 }}>
                  LED Color
                </label>
                <HexColorPicker color={editColor} onChange={setEditColor} style={{ width: '100%' }} />
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      backgroundColor: editColor,
                      border: '2px solid var(--border-color)',
                    }}
                  />
                  <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                    {editColor.toUpperCase()}
                  </span>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: 'var(--text-primary)' }}>
                <input
                  type="checkbox"
                  checked={editBlink}
                  onChange={(e) => setEditBlink(e.target.checked)}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 600 }}>Enable Blinking</span>
              </label>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, color: 'var(--text-primary)', fontWeight: 600 }}>
                Notifications
              </label>
              <button
                onClick={handleToggleMute}
                disabled={isUpdating}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  backgroundColor: stream.muted ? '#28a745' : '#ffc107',
                  color: stream.muted ? 'white' : '#000',
                  border: 'none',
                  borderRadius: 6,
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
                title={stream.muted ? 'Unmute notifications' : 'Mute notifications'}
              >
                {stream.muted ? '🔔 Unmute Notifications' : '🔕 Mute Notifications'}
              </button>
            </div>

            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 20, marginBottom: 20 }}>
              <button
                onClick={() => {
                  setShowModal(false);
                  onDelete(stream.id);
                }}
                disabled={isDeleting}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                {isDeleting ? 'Deleting...' : '🗑️ Delete Stream'}
              </button>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancelEdit}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveColor}
                disabled={isUpdating}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {isUpdating ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StreamItem;
